import { EventEmitter } from 'events';
import { ReadStream } from './read-stream.js';
import { WriteStream } from './write-stream.js';
import { promisify } from 'util';
import { readSocket } from './read-socket.js';
const GET_ARGS = 0x01;
const READ_STDIN = 0x02;
const WRITE_STDOUT = 0x03;
const WRITE_STDERR = 0x04;
const GET_CWD = 0x05;
const GET_ENV = 0x06;
const EXIT = 0x07;
const CLOSE_STDIN = 0x09;
const CLOSE_STDOUT = 0x0a;
const CLOSE_STDERR = 0x0b;
const IS_STDIN_CONNECTED = 0x0c;
const destroyIfNecessary = (...streams) => {
    streams.filter((x) => !x.destroyed).forEach((x) => x.destroy());
};
const buf = (length, fn) => {
    const buf = Buffer.alloc(length);
    fn(buf);
    return buf;
};
const uint8 = (num) => buf(1, (b) => b.writeUInt8(num, 0));
const uint32 = (num) => buf(4, (b) => b.writeUInt32LE(num, 0));
const int32 = (num) => buf(4, (b) => b.writeInt32LE(num, 0));
export class ProcessProxyConnection extends EventEmitter {
    socket;
    token;
    stdin;
    stdout;
    stderr;
    queue = Promise.resolve();
    hasSentExit = false;
    write;
    get closed() {
        return this.socket.closed;
    }
    constructor(socket, token) {
        super();
        this.socket = socket;
        this.token = token;
        this.stdin = new ReadStream(this.readStdin.bind(this), this.closeStream.bind(this, CLOSE_STDIN));
        this.stdout = new WriteStream(this.writeStream.bind(this, WRITE_STDOUT), this.closeStream.bind(this, CLOSE_STDOUT));
        this.stderr = new WriteStream(this.writeStream.bind(this, WRITE_STDERR), this.closeStream.bind(this, CLOSE_STDERR));
        this.socket.on('close', this.handleClose.bind(this));
        this.socket.on('error', this.handleError.bind(this));
        this.write = promisify(this.socket.write.bind(this.socket));
    }
    closeStream(cmd) {
        return this.send(cmd, [], { onConnectionClosed: () => Promise.resolve() });
    }
    handleClose() {
        destroyIfNecessary(this.stdin, this.stdout, this.stderr);
        this.emit('close');
    }
    handleError(error) {
        this.emit('error', error);
    }
    async read(length, fn) {
        return fn(await readSocket(this.socket, length));
    }
    readString = (length) => this.read(length, (buf) => buf.toString('utf8'));
    readLengthPrefixedString = () => this.readUInt32LE().then(this.readString);
    readUInt32LE = () => this.read(4, (buf) => buf.readUInt32LE(0));
    readInt32LE = () => this.read(4, (buf) => buf.readInt32LE(0));
    async readStdin(maxBytes) {
        return this.invoke(READ_STDIN, [uint32(maxBytes)], async () => {
            const available = await this.readInt32LE();
            // -1: stdin closed, 0: no data available
            if (available <= 0) {
                return available === 0 ? Buffer.alloc(0) : null;
            }
            return this.read(available, (buf) => buf);
        });
    }
    writeStream(cmd, data) {
        return this.send(cmd, [uint32(data.length), data]);
    }
    send(cmd, payload, opts) {
        return this.invoke(cmd, payload, () => Promise.resolve(), opts);
    }
    invoke(cmd, payload, readCb, opts) {
        const handleInvoke = async () => {
            await opts?.onBeforeSend?.();
            if (this.closed || this.hasSentExit) {
                if (opts?.onConnectionClosed) {
                    return opts.onConnectionClosed();
                }
            }
            await this.write(uint8(cmd));
            for (const p of payload) {
                await this.write(p);
            }
            const statusCode = await this.readInt32LE();
            if (statusCode !== 0) {
                const errorMsg = await this.readLengthPrefixedString();
                throw new Error(errorMsg || `Unknown error ${statusCode} from proxy`);
            }
            this.hasSentExit ||= cmd === EXIT;
            return readCb();
        };
        return (this.queue = this.queue.then(handleInvoke, handleInvoke));
    }
    async getArgs() {
        return this.invoke(GET_ARGS, [], async () => {
            const count = await this.readUInt32LE();
            const args = [];
            for (let i = 0; i < count; i++) {
                args.push(await this.readLengthPrefixedString());
            }
            return args;
        });
    }
    async getEnv() {
        return this.invoke(GET_ENV, [], async () => {
            const count = await this.readUInt32LE();
            const env = {};
            for (let i = 0; i < count; i++) {
                const envVar = await this.readLengthPrefixedString();
                const eqIndex = envVar.indexOf('=');
                if (eqIndex !== -1) {
                    const key = envVar.substring(0, eqIndex);
                    const value = envVar.substring(eqIndex + 1);
                    env[key] = value;
                }
            }
            return env;
        });
    }
    async getCwd() {
        return this.invoke(GET_CWD, [], () => this.readLengthPrefixedString());
    }
    async exit(code) {
        return this.send(EXIT, [int32(code)], {
            onBeforeSend: () => {
                // Destroy the streams just before sending the exit command
                // to ensure that any pending writes queued before calling exit()
                // has been sent to the proxy process.
                destroyIfNecessary(this.stdin, this.stdout, this.stderr);
                return Promise.resolve();
            },
            onConnectionClosed: () => {
                return Promise.reject(new Error('Connection already closed'));
            },
        });
    }
    async isStdinConnected() {
        return this.invoke(IS_STDIN_CONNECTED, [], this.readInt32LE).then(Boolean);
    }
}
